import { animated, useSpring } from '@react-spring/web';
import { useMemo, useState } from 'react';
import Select, {
  InputActionMeta,
  MenuPlacement,
  SingleValue,
} from 'react-select';

import { moveBottom } from '@theme/animations';
import baseTheme from '@theme/base';
import { useMobileWidth } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import Icon from '../Icon';

const IconContainer = styled.div`
  color: ${props => props.theme.color.primary};
  padding: 0.5rem;
`;
const RotateIcon = styled(animated.div)``;
const InputSelectRoot = styled.div<{ $isOpen: boolean }>`
  position: relative;
  z-index: ${({ $isOpen }) => ($isOpen ? 9999 : 'auto')};

  div[class$='menu'] {
    z-index: 9999;
    height: ${props => (props.$isOpen ? 'auto' : '0')};
    animation: all 600ms ease;
    overflow: hidden;
    background-color: ${props => props.theme.color.transparent};
    margin-top: 1rem;
  }
  div[class$='MenuList'] {
    animation: ${moveBottom} 600ms ease;
  }

  div[class$='singleValue'] {
    margin-bottom: 0rem;
    margin-top: 5px;
  }
`;
type OptionType = Record<string, any>;
type OptionsType = OptionType[];
export type ValueType = OptionType | OptionsType | null;

interface InputSelectProps {
  onChange: (value: any) => void;
  options: OptionsType;
  className?: string;
  value?: any;
  menuPlacement?: MenuPlacement;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  backgroundColor?: string;
  color?: string;
  selectedValueColor?: string;
  minWidth?: string;
  height?: string;
  fontSize?: string;
  maxWidth?: string;
  border?: string;
  borderRadius?: string;
  hoverColor?: string;
  isSearchable?: boolean;
  textAlign?: 'left' | 'center';
  focusBorder?: string;
}

interface StylesType {
  borderRadius?: string;
  backgroundColor?: string;
  color?: string;
  minWidth?: string;
  maxWidth?: string;
  disabled?: boolean;
  selectedValueColor?: string;
  height?: string;
  fontSize?: string;
  border?: string;
  textColor?: string;
  showSelectedValue?: boolean;
  isMobile?: boolean;
  textAlign?: 'left' | 'center';
  focusBorder?: string;
}
const getStyles = ({
  borderRadius,
  backgroundColor,
  color,
  minWidth,
  maxWidth,
  disabled,
  selectedValueColor,
  height,
  fontSize,
  border,
  textColor,
  showSelectedValue,
  isMobile,
  textAlign = 'center',
  focusBorder,
}: StylesType) => ({
  menu: (provided: any) => ({
    ...provided,
    marginTop: '0',
    backgroundColor,
    borderTopRightRadius: '0',
    borderTopLeftRadius: '0',
    boxShadow: 'none',
    top: '2.7rem',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    marginLeft: '1.4rem',
    visibility: showSelectedValue ? 'visible' : 'hidden',
    fontSize: fontSize ?? baseTheme.fontSize.size20,
    fontWeight: color && baseTheme.fontWeight.weight500,
    color: textColor,
    textAlign,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginBottom: 0,
    position: 'relative',
  }),
  container: (provided: any) => ({
    ...provided,
    ...(minWidth && { minWidth, width: '100%' }),
    ...(maxWidth && { maxWidth, width: '100%' }),
    height: height ?? '4rem',
  }),
  input: (provided: any) => ({
    ...provided,
    color: baseTheme.color.grey1,
    fontFamily: baseTheme.font.body,
    fontSize: fontSize ?? baseTheme.fontSize.size18,
    fontWeight: baseTheme.fontWeight.weight400,
    margin: 0,
    padding: 0,
    lineHeight: 1,
  }),
  placeholder: (provided: any) => ({
    ...provided,
    visibility: showSelectedValue ? 'visible' : 'hidden',
    padding: isMobile ? '0 1.8rem 0rem 0.5rem' : '0rem 0rem 0rem 0rem',
    textAlign,
    color: textColor,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: fontSize ?? baseTheme.fontSize.size18,
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    color:
      selectedValueColor ??
      (state.isFocused
        ? baseTheme.color.primary
        : (color ?? baseTheme.color.grey2)),
    ':hover': {
      color: selectedValueColor ?? baseTheme.color.primary,
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor:
      ((state.isFocused && !backgroundColor && baseTheme.color.white) ||
        (disabled && baseTheme.color.inputGrey) ||
        backgroundColor) ??
      baseTheme.color.white,
    color: color ?? baseTheme.color.grey1,
    borderRadius: borderRadius ?? baseTheme.borderRadius.radius10,
    border: state.isFocused
      ? (focusBorder ??
        border ??
        `0.1rem solid ${backgroundColor ?? baseTheme.color.mobilityGrey}`)
      : (border ??
        `0.1rem solid ${backgroundColor ?? baseTheme.color.mobilityGrey}`),
    minHeight: '4rem',
    height: '4rem',
    textAlign,
    boxShadow: 'none',
    ':hover': {
      borderColor: baseTheme.color.inputGrey,
    },
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  option: (provided: any) => ({
    ...provided,
    padding: '0.8rem 1rem',
    textAlign,
    color,
    backgroundColor,
    fontSize: fontSize ?? baseTheme.fontSize.size18,
    fontWeight: baseTheme.fontWeight.weight400,
    minHeight: '3rem',
    borderBottom: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    borderRadius: '0',
    ':hover': {
      color: baseTheme.color.primary,
    },
    ':active': {
      backgroundColor,
    },
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: '4rem',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: '4rem',
    padding: '0 1rem',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    paddingBottom: '1rem',
  }),
});

const NoOptionsMessage = styled.div<{ $backgroundColor?: string }>`
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ?? theme.color.mobilityGrey};
  padding: 0.8rem 0.5rem;
`;

const InputSelect = ({
  onChange,
  value,
  options,
  defaultValue,
  disabled = false,
  menuPlacement,
  borderRadius,
  backgroundColor,
  color,
  minWidth,
  maxWidth,
  height,
  selectedValueColor,
  fontSize,
  border,
  hoverColor,
  isSearchable,
  textAlign = 'center',
  focusBorder,
  ...restProps
}: InputSelectProps) => {
  const { t } = useTranslation();
  const [textColor, setTextColor] = useState(color);
  const [showSelectedValue, setShowSelectedValue] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const isMobile = useMobileWidth();

  const handleOnChange = (newValue: {
    value: SingleValue<OptionType>;
    label: string;
  }) => {
    if (onChange) {
      onChange(newValue?.value);
    }
    setInputValue(''); // Clear search input when selection is made
  };

  const onInputChange: (
    newValue: string,
    actionMeta: InputActionMeta
  ) => void = (newValue: string) => {
    setInputValue(newValue);
  };

  const selectedItem = options?.find(
    (option: OptionType) => option.value === value
  );
  const selectedValue = selectedItem?.value !== null ? selectedItem : null;
  const defaultOption =
    options?.find(option => option.value === defaultValue)?.value ?? '';

  const props = useSpring({
    from: { transform: 'rotateZ(0deg)' },
    to: { transform: 'rotateZ(-180deg)' },
    config: { duration: isOpen ? 600 : 0 },
    reverse: !isOpen,
  });

  const DropdownIndicator = () => {
    return (
      <IconContainer>
        <RotateIcon style={props}>
          <Icon size={18} color={textColor} name="caret_arrow" indentLess />
        </RotateIcon>
      </IconContainer>
    );
  };

  const styles = useMemo(
    () =>
      getStyles({
        borderRadius,
        backgroundColor,
        color,
        minWidth,
        maxWidth,
        disabled,
        selectedValueColor,
        height,
        fontSize,
        border,
        textColor,
        showSelectedValue,
        isMobile,
        textAlign,
        focusBorder,
      } satisfies StylesType),
    [
      borderRadius,
      backgroundColor,
      color,
      minWidth,
      maxWidth,
      disabled,
      selectedValueColor,
      height,
      fontSize,
      border,
      textColor,
      showSelectedValue,
      isMobile,
      textAlign,
      focusBorder,
    ]
  );

  return (
    <InputSelectRoot
      onMouseOver={() => {
        setTextColor(hoverColor);
      }}
      onMouseOut={() => {
        setTextColor(color);
      }}
      $isOpen={isOpen}
    >
      <Select
        value={selectedValue}
        onChange={handleOnChange}
        isDisabled={disabled}
        styles={styles}
        options={options}
        menuPlacement={menuPlacement}
        components={{ DropdownIndicator }}
        onInputChange={onInputChange}
        inputValue={inputValue}
        isSearchable={isSearchable}
        onFocus={() => {
          /** */
        }}
        onMenuClose={() => {
          setShowSelectedValue(true);
          setIsOpen(false);
        }}
        onMenuOpen={() => {
          setShowSelectedValue(false);
          setIsOpen(true);
        }}
        noOptionsMessage={() => (
          <NoOptionsMessage $backgroundColor={backgroundColor}>
            {t('no-options')}
          </NoOptionsMessage>
        )}
        {...restProps}
      />
    </InputSelectRoot>
  );
};

export default InputSelect;
